<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>Course Grades</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          v-model="search"
          :label="$i18n.translate('Filter')"
          light
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
      <v-menu offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" icon v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <CourseField showKey v-model="filterByCourse" :label="$i18n.translate('Course')" @input="onSubmit" />
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-btn
        v-if="$privilege.hasPrivilege('COURSE_GRADE_CREATE')"
        fab
        small
        :to="{ name: 'courseGrade', params: { id: 0 } }"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-data-table
        :items="items"
        :headers="headers"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
      >
        <template v-slot:item.id="{ item }">
          <router-link :to="{ name: 'courseGrade', params: { id: item.id } }">
            {{ item.id }}
          </router-link>
        </template>
        <template v-slot:item.course="{ item }">
          <router-link
            v-if="$privilege.hasPrivilege('COURSE_READ')"
            :to="{ name: 'course', params: { id: item.course.id } }"
            >{{ item.course.name }}</router-link
          >
          <span v-else>{{ item.course.name }}</span>
        </template>
        <template v-slot:item.participant="{ item }">
          <router-link
            v-if="$privilege.hasPrivilege('PARTICIPANT_READ')"
            :to="{ name: 'participant', params: { id: item.participant.id } }"
            >({{ item.participant.participantKey }}) {{ item.participant.fullName }}</router-link
          >
          <span v-else>({{ item.participant.participantKey }}) {{ item.participant.fullName }}</span>
        </template>
        <template v-slot:item.effective="{ item }">
          <v-chip color="primary" v-if="item.effective">{{ $i18n.translate("Active") }}</v-chip>
          <v-chip color="danger" v-else>{{ $i18n.translate("Inactive") }}</v-chip>
        </template>
        <template v-slot:item.completedDate="{ item }">
          {{ item.completedDate | formatDateClient("MM/DD/YYYY hh:mm a z", selectedClient) }}<br />
          {{ item.completedDate | formatDateFromNow }}
        </template>
        <template v-slot:item.updatedDate="{ item }">
          {{ item.updatedDate | formatDateFromNow }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import CourseField from "@/gapp-components/components/fields/CourseField.vue";
import debounce from "lodash/debounce";

export default {
  name: "CourseGrades",
  metaInfo: {
    title: "Course Grades"
  },
  components: {
    CourseField
  },
  data() {
    return {
      loading: false,
      items: [],
      nameFilter: "",
      search: "",
      filterByCourse: null,
      headers: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        sortBy: ["updatedDate"],
        sortDesc: [true]
      }
    };
  },
  watch: {
    options: {
      handler() {
        this.debouncedGetData();
      },
      deep: true
    }
  },
  created() {
    this.debouncedGetData = debounce(() => {
      this.getData();
    }, 200);
  },
  methods: {
    initHeaders() {
      let h = [
        {
          value: "id",
          text: "ID",
          sortable: true
        },
        {
          value: "course",
          text: this.$i18n.translate("Course"),
          sortable: true
        },
        {
          value: "participant",
          text: this.$i18n.translate("Participant"),
          sortable: true
        },
        {
          value: "grade",
          text: this.$i18n.translate("Grade"),
          sortable: true,
          align: "right"
        },
        {
          value: "completedDate",
          text: "Completed?",
          align: "center"
        },
        {
          value: "updatedDate",
          text: "Last Updated",
          sortable: true
        }
      ];
      this.headers = h;
    },
    onSubmit() {
      this.options.page = 1;
      this.debouncedGetData();
    },
    getData() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      let filters = {};
      if (this.search && this.search.trim().length > 0) {
        filters.keyword = this.search;
      }

      if (this.filterByCourse !== null) {
        filters.course = this.filterByCourse;
      }

      filters.isEffective = "Active";

      this.$api
        .post(
          "/api/courseGrades/search?size=" +
            itemsPerPage +
            "&page=" +
            (page - 1) +
            (sortBy && sortBy.length > 0
              ? "&sort=" + sortBy[0] + "," + ((sortDesc && sortDesc.length > 0) & sortDesc[0] ? "DESC" : "ASC")
              : ""),
          filters
        )
        .then(({ data }) => {
          this.loading = false;
          this.items = data.content;
          this.total = data.totalElements;
        })
        .catch(error => {
          this.loading = false;
          console.log(error);
        });
    },
    onClear() {
      this.search = "";
      this.filterByCourse = null;
      this.debouncedGetData();
    }
  },
  mounted() {
    this.initHeaders();
    this.$store.dispatch("setBreadcrumb", [
      {
        text: this.$i18n.translate("Dashboard"),
        to: { name: "dashboard" },
        exact: true
      },
      {
        text: this.$i18n.translate("Clients"),
        to: { name: "clients" },
        exact: true
      },
      {
        text: this.selectedClient.name,
        to: {
          name: "client",
          params: { id: this.selectedClient.id },
          exact: true
        }
      },
      {
        text: this.$i18n.translate("Programs"),
        to: { name: "programs" },
        exact: true
      },
      {
        text: this.selectedProgram.name,
        to: {
          name: "program",
          params: { id: this.selectedProgram.id },
          exact: true
        }
      },
      { text: "Course Grades" }
    ]);
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"])
  }
};
</script>
